import { Either, ParseResult, Schema } from "effect";
import { parse, stringify } from "yaml";

// ADR-0118 — the persona registry as pack data.
//
// A pack's `personas.yml` is the machine-read half of its cast: identity, role, the gender/age the
// grammar checks resolve against, the appearance prose and lighting the image model is prompted with,
// and the ElevenLabs voice TTS speaks with. The prose bios the prompts read stay in `personas.md` /
// `tutors.md`. `L2_<code>/personas.yml` holds the scene cast; `L1_<code>/personas.yml` holds the tutors,
// who speak the L1. A run's registry is the two merged.
//
// Each persona's identity-kit images are BINARY pack documents at
// `<pack>/personas/<id>/<slot>.<ext>` — see `LanguagePackBlobPath` below and the
// `LanguagePackBlobStore` port.
//
// Pure: parsing, rendering and path arithmetic only. It never touches a store.

/** The file every pack's persona registry lives in, relative to its `L1_`/`L2_` directory. */
export const PERSONAS_FILE = "personas.yml";

/** `L2_QMS` -> `L2_QMS/personas.yml`. */
export const personasFilePath = (packDir: string): string => `${packDir}/${PERSONAS_FILE}`;

// The nine identity-kit roles `persona-config` shipped, in the order a human thinks about them.
// `origin` is the one image generation actually sends to the model as the identity reference
// (`media_enrichment.image._identity_kit_origin_bytes`); `avatar` is the fallback head the UI shows.
export const PERSONA_IMAGE_SLOTS = [
  "origin",
  "avatar",
  "front_face_anchor",
  "three_quarter_face_anchor",
  "smiling_face_anchor",
  "half_body",
  "full_side_profile",
  "full_back_view",
  "full_body_front",
] as const;
export const PersonaImageSlot = Schema.Literal(...PERSONA_IMAGE_SLOTS);
export type PersonaImageSlot = typeof PersonaImageSlot.Type;

/** What each slot is for, in the words the Personas view and the template README use. */
export const PERSONA_IMAGE_SLOT_LABELS: Readonly<Record<PersonaImageSlot, string>> = {
  origin: "Origin — the identity reference sent to the image model",
  avatar: "Avatar — the small head shown in the learner app",
  front_face_anchor: "Front face",
  three_quarter_face_anchor: "Three-quarter face",
  smiling_face_anchor: "Smiling face",
  half_body: "Half body",
  full_side_profile: "Full side profile",
  full_back_view: "Full back view",
  full_body_front: "Full body, front",
};

// Extension -> MIME type. The extension is part of the path, so the path alone says how to serve it.
export const PERSONA_IMAGE_TYPES = {
  webp: "image/webp",
  png: "image/png",
  jpg: "image/jpeg",
} as const;
export type PersonaImageExtension = keyof typeof PERSONA_IMAGE_TYPES;
export type PersonaImageMimeType = (typeof PERSONA_IMAGE_TYPES)[PersonaImageExtension];

/** Largest identity-kit image the store accepts. The current kit's largest is well under 1 MiB. */
export const MAX_PERSONA_IMAGE_BYTES = 5 * 1024 * 1024;

// A persona id: lowercase, the form stored exercise media already carries (`juan`, `juan_carlos`).
const PERSONA_ID_PATTERN = /^[a-z][a-z0-9_]*$/;
export const PersonaId = Schema.String.pipe(
  Schema.pattern(PERSONA_ID_PATTERN, {
    message: () => "a persona id is lowercase letters, digits and underscores, e.g. `juan`",
  }),
  Schema.brand("PersonaId"),
);
export type PersonaId = typeof PersonaId.Type;

const BLOB_PATH_PATTERN = new RegExp(
  `^(L[12]_[A-Za-z]+)/personas/([a-z][a-z0-9_]*)/(${PERSONA_IMAGE_SLOTS.join("|")})\\.(${Object.keys(
    PERSONA_IMAGE_TYPES,
  ).join("|")})$`,
);

// A `LanguagePackBlobPath` is the one shape a binary pack document may have. Closed by a single
// anchored pattern, so it is a traversal guard by construction: no `..`, no absolute path, no
// percent-encoding and no extension outside the allowlist can match it.
export const LanguagePackBlobPath = Schema.String.pipe(
  Schema.filter(
    (s) =>
      BLOB_PATH_PATTERN.test(s) ||
      "a pack image path is `L1_<code>|L2_<code>/personas/<persona id>/<slot>.<webp|png|jpg>`",
  ),
  Schema.brand("LanguagePackBlobPath"),
);
export type LanguagePackBlobPath = typeof LanguagePackBlobPath.Type;

export interface PersonaImagePathParts {
  readonly packDir: string;
  readonly personaId: PersonaId;
  readonly slot: PersonaImageSlot;
  readonly extension: PersonaImageExtension;
}

const isSlot = (s: string): s is PersonaImageSlot =>
  (PERSONA_IMAGE_SLOTS as readonly string[]).includes(s);
const isExtension = (s: string): s is PersonaImageExtension => s in PERSONA_IMAGE_TYPES;

/** Split an image path into its pack, persona, slot and extension — `undefined` if it is not one. */
export const parsePersonaImagePath = (path: string): PersonaImagePathParts | undefined => {
  const match = BLOB_PATH_PATTERN.exec(path);
  if (match === null) return undefined;
  const [, packDir, id, slot, extension] = match;
  if (
    packDir === undefined ||
    id === undefined ||
    slot === undefined ||
    extension === undefined ||
    !isSlot(slot) ||
    !isExtension(extension)
  )
    return undefined;
  return { packDir, personaId: PersonaId.make(id), slot, extension };
};

export const personaImagePath = (
  packDir: string,
  personaId: string,
  slot: PersonaImageSlot,
  extension: PersonaImageExtension,
): LanguagePackBlobPath =>
  LanguagePackBlobPath.make(`${packDir}/personas/${personaId}/${slot}.${extension}`);

/** The `L1_<code>`/`L2_<code>` directory an image lives under — what `list(languageCode)` filters on. */
export const languagePackBlobCodeOf = (path: LanguagePackBlobPath): string =>
  path.split("/")[0] ?? "";

export const mimeTypeOfBlobPath = (path: LanguagePackBlobPath): PersonaImageMimeType => {
  const extension = path.slice(path.lastIndexOf(".") + 1);
  return isExtension(extension) ? PERSONA_IMAGE_TYPES[extension] : "image/webp";
};

// Longest first, so a name spelling a long slot is never read as a shorter one inside it.
const SLOTS_LONGEST_FIRST = [...PERSONA_IMAGE_SLOTS].sort((a, b) => b.length - a.length);

/**
 * The slot a dropped photo's filename names, or `undefined` when it names none.
 *
 * Case, separators and the extension are ignored (`Juan - Front Face Anchor.PNG` is
 * `front_face_anchor`), but a slot must be whole words of the name: `originals.webp` is not
 * `origin`. Used to place a whole kit dropped in one go; a photo it cannot place is left to the human.
 */
export const slotOfImageFileName = (name: string): PersonaImageSlot | undefined => {
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const words = `_${stem
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")}_`;
  return SLOTS_LONGEST_FIRST.find((slot) => words.includes(`_${slot}_`));
};

/** The extension a MIME type is stored under, for an upload that names a type and not a file. */
export const extensionOfMimeType = (mime: string): PersonaImageExtension | undefined =>
  (Object.keys(PERSONA_IMAGE_TYPES) as PersonaImageExtension[]).find(
    (extension) => PERSONA_IMAGE_TYPES[extension] === mime,
  );

/**
 * Whether the bytes are actually the image type their path claims.
 *
 * Checked on every write: the path decides the `Content-Type` the image is later served with and the
 * MIME type the pipeline hands the image model, so a PNG saved as `.webp` would be mislabelled all the
 * way to Gemini. Magic numbers only — enough to refuse a renamed file, not a full decode.
 */
export const imageBytesMatchType = (bytes: Uint8Array, mime: PersonaImageMimeType): boolean => {
  const startsWith = (offset: number, signature: readonly number[]): boolean =>
    signature.every((byte, index) => bytes[offset + index] === byte);
  switch (mime) {
    case "image/png":
      return startsWith(0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
    case "image/jpeg":
      return startsWith(0, [0xff, 0xd8, 0xff]);
    case "image/webp":
      // "RIFF" <size> "WEBP"
      return startsWith(0, [0x52, 0x49, 0x46, 0x46]) && startsWith(8, [0x57, 0x45, 0x42, 0x50]);
  }
};

/**
 * Normalise an image path as a directory picker hands it over into a pack-relative image path, the
 * image twin of `packRelativePath`: every segment before the first `L1_`/`L2_` root is dropped, so
 * picking `packs/`, one `L2_FRA/` or a folder with any name lands on the same path. `undefined` when
 * the file is not a persona image.
 */
export const packRelativeImagePath = (rawPath: string): LanguagePackBlobPath | undefined => {
  const segments = rawPath.split("/").filter((segment) => segment.length > 0);
  const rootIndex = segments.findIndex((segment) => /^L[12]_[A-Za-z]+$/.test(segment));
  if (rootIndex < 0) return undefined;
  const relative = segments.slice(rootIndex).join("/");
  // Pickers preserve the extension's case; the path brand is lowercase-only.
  const dot = relative.lastIndexOf(".");
  const normalised = `${relative.slice(0, dot)}${relative.slice(dot).toLowerCase()}`.replace(
    /\.jpeg$/,
    ".jpg",
  );
  return BLOB_PATH_PATTERN.test(normalised) ? LanguagePackBlobPath.make(normalised) : undefined;
};

/**
 * Why an image may not be stored at `path`, or `undefined` if it may. One rule for the upload form,
 * the Personas view and the server, so a refusal reads the same wherever it happens.
 */
export const personaImageProblem = (
  path: LanguagePackBlobPath,
  bytes: Uint8Array,
): string | undefined => {
  if (bytes.byteLength === 0) return "The image is empty.";
  if (bytes.byteLength > MAX_PERSONA_IMAGE_BYTES)
    return `The image is ${(bytes.byteLength / 1024 / 1024).toFixed(1)} MB; the limit is ${MAX_PERSONA_IMAGE_BYTES / 1024 / 1024} MB.`;
  const mime = mimeTypeOfBlobPath(path);
  if (!imageBytesMatchType(bytes, mime))
    return `The file is not a ${mime.replace("image/", "").toUpperCase()} image, though its name says it is.`;
  return undefined;
};

// One stored image as the store describes it — everything but the bytes, so a list is cheap.
export const LanguagePackBlob = Schema.Struct({
  path: LanguagePackBlobPath,
  languageCode: Schema.String,
  mimeType: Schema.String,
  sha256: Schema.String,
  size: Schema.Number,
  // Bumped on every replace. `put` takes an optional expected revision, so a replace can be
  // compare-and-swap like a document write.
  revision: Schema.Number,
  updatedAt: Schema.DateTimeUtc,
});
export type LanguagePackBlob = typeof LanguagePackBlob.Type;

// ── personas.yml ────────────────────────────────────────────────────────────────

export const PersonaRole = Schema.Literal("main-character", "tutor", "extra");
export type PersonaRole = typeof PersonaRole.Type;

// Grammatical gender, which is what every consumer of it needs (agreement checks, the labeler's
// persona list, self-reference audits).
export const PersonaGender = Schema.Literal("male", "female");
export type PersonaGender = typeof PersonaGender.Type;

export const PersonaAge = Schema.Literal("young", "middle-aged", "older");
export type PersonaAge = typeof PersonaAge.Type;

// ElevenLabs' accepted speed range.
const VoiceSpeed = Schema.optionalWith(Schema.Number.pipe(Schema.between(0.7, 1.2)), {
  default: () => 1,
});

export const CourseVoice = Schema.Struct({
  voice_id: Schema.NonEmptyTrimmedString,
  speed: VoiceSpeed,
});
export type CourseVoice = typeof CourseVoice.Type;

export const PersonaVoice = Schema.Struct({
  provider: Schema.Literal("elevenlabs"),
  voice_id: Schema.NonEmptyTrimmedString,
  speed: VoiceSpeed,
  // ADR-0149: L2 code -> the voice a tutor speaks with in that course, in place of the one above. A
  // course without one uses the one above.
  by_l2: Schema.optional(Schema.Record({ key: Schema.String, value: CourseVoice })),
});
export type PersonaVoice = typeof PersonaVoice.Type;

/** The voice a persona speaks an `l2` course with: its voice for that course, else its default. */
export const voiceForL2 = (voice: PersonaVoice, l2: string): CourseVoice =>
  voice.by_l2?.[l2] ?? voice;

export const Persona = Schema.Struct({
  id: PersonaId,
  display_name: Schema.NonEmptyTrimmedString,
  // Other names the scripts call this persona by ("Resi" for Theresa). A dialogue speaker labelled
  // with one is voiced and drawn as this persona, not left to the fallback pick.
  aliases: Schema.optional(Schema.Array(Schema.NonEmptyTrimmedString)),
  role: PersonaRole,
  gender: PersonaGender,
  age: PersonaAge,
  // A short label such as "Young adult female", used where a one-line gloss of the persona is shown.
  type: Schema.optional(Schema.String),
  // The appearance prose the image model is prompted with.
  description: Schema.String,
  // Overrides the file-level `lighting` for this persona.
  lighting: Schema.optional(Schema.String),
  // Absent means TTS falls back to the default voice — reported as a warning, not refused.
  voice: Schema.optional(PersonaVoice),
  // A deprecated persona is an alias kept only so stored content carrying its id still resolves. It
  // is never offered as cast.
  deprecated: Schema.optionalWith(Schema.Boolean, { default: () => false }),
  alias_of: Schema.optional(PersonaId),
  // The one current main character of an L2 cast who voices audio and draws images whose media names
  // no persona. Exactly one per L2 pack; `checkLanguagePack` blocks on none or several.
  default_narrator: Schema.optionalWith(Schema.Boolean, { default: () => false }),
});
export type Persona = typeof Persona.Type;

export const PersonasFile = Schema.Struct({
  // The lighting spec shared by every persona in the file unless one overrides it.
  lighting: Schema.optional(Schema.String),
  personas: Schema.Array(Persona),
});
export type PersonasFile = typeof PersonasFile.Type;

/**
 * Parse a `personas.yml`. `Left` carries human-readable problems (a YAML syntax error, or every
 * schema violation with its path), so the upload report and the editor can show them verbatim.
 */
export const parsePersonasFile = (
  content: string,
): Either.Either<PersonasFile, readonly string[]> => {
  let raw: unknown;
  try {
    raw = parse(content);
  } catch (error) {
    return Either.left([
      `not valid YAML: ${error instanceof Error ? error.message : String(error)}`,
    ]);
  }
  return Schema.decodeUnknownEither(PersonasFile)(raw ?? {}, { errors: "all" }).pipe(
    Either.mapLeft((error) =>
      ParseResult.ArrayFormatter.formatErrorSync(error).map(
        (issue) => `${issue.path.length === 0 ? "" : `${issue.path.join(".")}: `}${issue.message}`,
      ),
    ),
  );
};

// Key order in the rendered file — the order a human reads a persona in, not alphabetical.
const PERSONA_KEY_ORDER = [
  "id",
  "display_name",
  "aliases",
  "role",
  "gender",
  "age",
  "type",
  "voice",
  "deprecated",
  "alias_of",
  "default_narrator",
  "lighting",
  "description",
] as const;

const HEADER = [
  "# The persona registry for this pack (ADR-0118). Machine-read — keep the keys as they are.",
  "#",
  "# id           lowercase; the id stored exercise media carries. Do not rename an existing one.",
  "# aliases      other names the scripts call the persona by (Resi for Theresa); optional",
  "# role         main-character | extra (L2 packs) · tutor (L1 packs)",
  "# gender       male | female — grammatical gender, used by agreement checks",
  "# age          young | middle-aged | older",
  "# voice        ElevenLabs voice_id and speed (0.7–1.2, default 1); a tutor's by_l2 maps an L2",
  "#              code to the voice_id/speed it speaks that course with (absent: the default)",
  "# description  appearance prose sent to the image model",
  "# deprecated   true for an alias kept only so stored content still resolves; set alias_of",
  "# default_narrator  true on exactly one current main-character of an L2 pack: the persona who",
  "#              voices audio and is drawn in images whose media names no persona",
  "#",
  "# Identity-kit images live beside this file at personas/<id>/<slot>.webp|png|jpg, slots:",
  `#   ${PERSONA_IMAGE_SLOTS.join(", ")}`,
  "",
].join("\n");

/**
 * Render a registry back to YAML — what the Personas view writes when a human saves the form.
 *
 * Comments in a hand-edited file are not preserved (the header is re-emitted); everything the schema
 * reads round-trips. Encoding goes through the schema first, so a default (`speed: 1`,
 * `deprecated: false`) is written out explicitly rather than silently implied. `default_narrator` is the
 * exception: it is written only on the one persona it is true for.
 */
export const renderPersonasFile = (file: PersonasFile): string => {
  const encoded = Schema.encodeSync(PersonasFile)(file);
  const ordered = {
    ...(encoded.lighting === undefined ? {} : { lighting: encoded.lighting }),
    personas: encoded.personas.map((persona) => {
      const record: Record<string, unknown> = persona;
      const out: Record<string, unknown> = {};
      for (const key of PERSONA_KEY_ORDER) {
        if (record[key] === undefined) continue;
        if (key === "default_narrator" && record[key] === false) continue;
        out[key] = record[key];
      }
      return out;
    }),
  };
  return `${HEADER}${stringify(ordered, { lineWidth: 100, blockQuote: "literal" })}`;
};

/**
 * The registry with `persona` in place of the entry that had id `replacing` (or appended when there
 * was none). `persona.id` may differ from `replacing` — a rename — and then must not collide with an
 * existing one; every alias of the old id is repointed at the new one, so none is left naming an id
 * the file no longer defines. Marking `persona` the `default_narrator` unmarks every other persona.
 */
export const upsertPersona = (
  file: PersonasFile,
  persona: Persona,
  replacing: string | undefined,
): Either.Either<PersonasFile, string> => {
  const clash = file.personas.some((p) => p.id === persona.id && p.id !== replacing);
  if (clash) return Either.left(`A persona with id \`${persona.id}\` already exists.`);
  const index = replacing === undefined ? -1 : file.personas.findIndex((p) => p.id === replacing);
  // A pack has one default narrator, so marking this persona unmarks whoever held it.
  const renamed = replacing !== undefined && replacing !== persona.id;
  const others = file.personas.map((p) => ({
    ...p,
    ...(persona.default_narrator && p.default_narrator ? { default_narrator: false } : {}),
    ...(renamed && p.alias_of === replacing ? { alias_of: persona.id } : {}),
  }));
  const personas =
    index < 0 ? [...others, persona] : others.map((p, i) => (i === index ? persona : p));
  return Either.right({ ...file, personas });
};

/**
 * The registry without persona `id`. Refused while another persona is an alias of it: deleting the
 * target would leave that alias pointing nowhere, which `checkLanguagePack` blocks.
 */
export const removePersona = (
  file: PersonasFile,
  id: string,
): Either.Either<PersonasFile, string> => {
  const aliases = file.personas.filter((p) => p.alias_of === id).map((p) => p.id);
  if (aliases.length > 0)
    return Either.left(
      `\`${aliases.join("`, `")}\` ${aliases.length === 1 ? "is an alias" : "are aliases"} of \`${id}\` — delete or repoint ${aliases.length === 1 ? "it" : "them"} first.`,
    );
  return Either.right({ ...file, personas: file.personas.filter((p) => p.id !== id) });
};

/** The personas a pack offers as cast: non-deprecated, with the given role. */
export const activePersonas = (file: PersonasFile, role: PersonaRole): readonly Persona[] =>
  file.personas.filter((persona) => persona.role === role && !persona.deprecated);
